import {useInfiniteQuery, UseInfiniteQueryOptions} from '@tanstack/react-query'
import {
    httpGetConversations,
    HttpGetConversationsOptions,
    INTERNAL_CONVERSATIONS_PATIENT_ID_PARAM
} from '../http/conversations.http'
import {QUERY_KEYS} from '@/queryClient'
import {ConversationsList} from '@/features/conversations/types'
import {infiniteQueryFetchNextPage} from '@/utilities/helpers'

/**
 * Hook to fetch conversations with pagination
 * @param patientId ID of the patient to get conversations for, or null for internal conversations (no patient)
 * @param perPage Number of results per page (defaults to 30)
 * @param search Optional search query to filter conversations
 * @param options Additional query options
 */
export const useConversations = (
    patientId: string | null,
    perPage: number = 30,
    search?: string,
    entityType?: HttpGetConversationsOptions['params']['entityType'],
    options?: Pick<UseInfiniteQueryOptions<ConversationsList>, 'enabled'>
) => {
    // Only include search in query key when it's actually used
    const normalizedSearch = search && search.trim() ? search.trim() : undefined

    const query = useInfiniteQuery({
        queryKey: [QUERY_KEYS.CONVERSATIONS, patientId, perPage, normalizedSearch, entityType],
        queryFn: ({pageParam = 1}) =>
            httpGetConversations({
                params: {
                    patientId: patientId === null ? INTERNAL_CONVERSATIONS_PATIENT_ID_PARAM : patientId,
                    entityType,
                    page: pageParam,
                    limit: perPage,
                    ...(normalizedSearch && {search: normalizedSearch})
                }
            }),
        initialPageParam: 1,
        // Enabled with a concrete patient id or in internal mode (explicit null); an empty string keeps it disabled
        enabled: patientId === null || !!patientId,
        ...options,
        getNextPageParam: lastPage => {
            const meta = lastPage.data.meta
            return meta.currentPage < meta.lastPage ? meta.currentPage + 1 : null
        }
    })

    return {
        ...query,
        fetchNextPage: () => infiniteQueryFetchNextPage(query),
        conversations: query.data?.pages.flatMap(page => page.data.data) || [],
        entityType
    }
}
